/**
 * Carrying a free prompt generation ("Track these personas") through signup,
 * email verification and onboarding, and remembering a skipped import.
 *
 * The generation id travels in `redirectTo`
 * (`/app/prompt-generator-claim/<id>`), but a signup can leave that URL:
 * email verification opens in a new tab, a social provider round-trips
 * through its own pages, and onboarding only ever sees what the middleware
 * forwarded. So the signup page also stashes `{ generationId, timestamp }`
 * in localStorage (the same pattern as `signup-plan-data`), and onboarding
 * reads it back within 24 hours.
 *
 * Pure: every function takes the storage it works on, so the tests pass an
 * in-memory one. `browserStorage()` is the real one (null on the server or
 * when storage is blocked).
 */

import {
	parsePromptGeneratorClaimPath,
	promptGenerationClaimExpiresAt,
} from "@repo/core/client";
import { isSafeRedirectPath } from "@saas/auth/lib/auth-redirects";

export const CLAIM_STASH_KEY = "signup-claim-generation";
/** A signup stash older than this is ignored (and removed). */
export const CLAIM_STASH_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** Per property: a generation whose import was skipped, offered again later. */
export const SKIPPED_IMPORT_KEY_PREFIX = "prompt-generator-skipped-import:";

/** Generation ids are cuids; anything else is refused rather than escaped. */
const GENERATION_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

export type ClaimStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** localStorage, or null on the server / when the browser blocks it. */
export function browserStorage(): ClaimStorage | null {
	try {
		return typeof window !== "undefined" ? window.localStorage : null;
	} catch {
		return null;
	}
}

export function isGenerationIdLike(value: unknown): value is string {
	return typeof value === "string" && GENERATION_ID_PATTERN.test(value);
}

/**
 * The generation id in a `redirectTo`, or null unless it is a safe,
 * app-relative claim path with a well-formed id.
 */
export function claimGenerationIdFromRedirect(
	redirectTo: string | null | undefined,
): string | null {
	if (!isSafeRedirectPath(redirectTo)) {
		return null;
	}
	const id = parsePromptGeneratorClaimPath(redirectTo as string);
	return isGenerationIdLike(id) ? id : null;
}

/** `redirectTo` when it is safe to hand to better-auth or the router. */
export function safeRedirectPath(
	redirectTo: string | null | undefined,
): string | null {
	return isSafeRedirectPath(redirectTo) ? (redirectTo as string) : null;
}

function safely<T>(fn: () => T, fallback: T): T {
	try {
		return fn();
	} catch {
		return fallback;
	}
}

export function writeClaimStash(
	storage: ClaimStorage | null,
	generationId: string,
	now: number = Date.now(),
): void {
	if (!storage || !isGenerationIdLike(generationId)) {
		return;
	}
	safely(
		() =>
			storage.setItem(
				CLAIM_STASH_KEY,
				JSON.stringify({ generationId, timestamp: now }),
			),
		undefined,
	);
}

export function clearClaimStash(storage: ClaimStorage | null): void {
	if (storage) {
		safely(() => storage.removeItem(CLAIM_STASH_KEY), undefined);
	}
}

/**
 * The stashed generation id, or null. An expired or malformed stash is
 * removed on read.
 */
export function readClaimStash(
	storage: ClaimStorage | null,
	now: number = Date.now(),
): string | null {
	if (!storage) {
		return null;
	}
	const raw = safely(() => storage.getItem(CLAIM_STASH_KEY), null);
	if (!raw) {
		return null;
	}
	const parsed = safely(
		() =>
			JSON.parse(raw) as {
				generationId?: unknown;
				timestamp?: unknown;
			} | null,
		null,
	);
	const fresh =
		typeof parsed?.timestamp === "number" &&
		now - parsed.timestamp >= 0 &&
		now - parsed.timestamp <= CLAIM_STASH_MAX_AGE_MS;
	if (!fresh || !isGenerationIdLike(parsed?.generationId)) {
		clearClaimStash(storage);
		return null;
	}
	return parsed.generationId as string;
}

/**
 * The generation to resume in onboarding: the claim path in `redirectTo`
 * (the middleware forwards it), else a fresh signup stash.
 */
export function resolveOnboardingClaim(
	redirectTo: string | null | undefined,
	storage: ClaimStorage | null,
	now: number = Date.now(),
): string | null {
	return (
		claimGenerationIdFromRedirect(redirectTo) ??
		readClaimStash(storage, now)
	);
}

/* ------------------------------------------------------------------ */
/* Skipped imports                                                     */
/* ------------------------------------------------------------------ */

export interface SkippedImport {
	generationId: string;
	/** ISO date the generation was made (its claim window runs from it). */
	generationCreatedAt: string;
}

function skippedKey(propertyId: string): string {
	return `${SKIPPED_IMPORT_KEY_PREFIX}${propertyId}`;
}

export function writeSkippedImport(
	storage: ClaimStorage | null,
	propertyId: string,
	skipped: SkippedImport,
): void {
	if (!storage || !isGenerationIdLike(skipped.generationId)) {
		return;
	}
	safely(
		() => storage.setItem(skippedKey(propertyId), JSON.stringify(skipped)),
		undefined,
	);
}

export function clearSkippedImport(
	storage: ClaimStorage | null,
	propertyId: string,
): void {
	if (storage) {
		safely(() => storage.removeItem(skippedKey(propertyId)), undefined);
	}
}

/**
 * The generation this property skipped importing, while it can still be
 * imported. Removed on read once the claim window has passed.
 */
export function readSkippedImport(
	storage: ClaimStorage | null,
	propertyId: string,
	now: number = Date.now(),
): SkippedImport | null {
	if (!storage) {
		return null;
	}
	const raw = safely(() => storage.getItem(skippedKey(propertyId)), null);
	if (!raw) {
		return null;
	}
	const parsed = safely(
		() => JSON.parse(raw) as Partial<SkippedImport> | null,
		null,
	);
	const createdAt =
		typeof parsed?.generationCreatedAt === "string"
			? parsed.generationCreatedAt
			: null;
	const open =
		createdAt !== null &&
		promptGenerationClaimExpiresAt(createdAt).getTime() > now;
	if (!open || !isGenerationIdLike(parsed?.generationId)) {
		clearSkippedImport(storage, propertyId);
		return null;
	}
	return {
		generationId: parsed.generationId as string,
		generationCreatedAt: createdAt as string,
	};
}
